import { request } from 'api/request';
import { Condition, ConditionComparator, ConditionType } from 'api/types';

export type AttributeFieldConfig = {
  attributeShortId: string;
  instructions: string;
};

export type JourneyEmailTriggerList = {
  shortId: string;
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
  isConfigured: boolean;
  journeyId: string;
};

export type JourneyEmailTrigger = JourneyEmailTriggerList & {
  triggerEmail: string;
  allowDuplicateTravellers: boolean;
  extractionFields: AttributeFieldConfig[];
  conditions?: {
    conditions: Condition[];
    conditionType: ConditionType;
  };
  logsCount: number;
};

export type JourneyEmailTriggerData = Partial<
  Omit<JourneyEmailTrigger, 'shortId' | 'triggerEmail' | 'logsCount' | 'isConfigured'>
>;

export const loadEmailTriggers = async (journeyId: string) => {
  const response = await request.get<JourneyEmailTriggerList[]>(
    `v1/email-triggers/?journey_id=${journeyId}`
  );
  return response.data;
};

export const loadEmailTrigger = async (shortId: string) => {
  const response = await request.get<JourneyEmailTrigger>(`v1/email-triggers/${shortId}/`);
  return response.data;
};

export const createEmailTrigger = async (journeyId: string) => {
  const response = await request.post<JourneyEmailTrigger>('v1/email-triggers/', {
    journeyId,
  });
  return response.data;
};

export const updateEmailTrigger = async (shortId: string, data: JourneyEmailTriggerData) => {
  const response = await request.patch<JourneyEmailTrigger>(`v1/email-triggers/${shortId}/`, data);
  return response.data;
};

export const deleteEmailTrigger = async (shortId: string) => {
  const response = await request.delete<void>(`v1/email-triggers/${shortId}/`);
  return response.data;
};

export type ExtractedFieldResult = {
  label: string;
  value: string;
};

export type GeneratedFieldInstruction = {
  attributeShortId: string;
  label: string;
  attributeType: string;
  instructions: string;
  extractedValue: string;
  source: 'traveller' | 'journey';
};

export type GenerateInstructionsPayload = { bodyText: string } | { logShortId: string };

export const generateInstructions = async (
  shortId: string,
  payload: GenerateInstructionsPayload
) => {
  const response = await request.post<{ fields: GeneratedFieldInstruction[] }>(
    `v1/email-triggers/${shortId}/generate-instructions/`,
    payload
  );
  return response.data.fields;
};

export type JourneyEmailTriggerLogList = {
  shortId: string;
  triggeredOnDate: string;
  triggeredOnTime: string;
  errorMessage: string;
};

export type ConditionResult = {
  field: string;
  comparator: ConditionComparator;
  value: string;
  receivedValue: string;
  success: boolean;
};

export type JourneyEmailTriggerLog = JourneyEmailTriggerLogList & {
  sender: string;
  subject: string;
  bodyPlain: string;
  extractedFieldsDisplay: ExtractedFieldResult[];
  conditionResults: ConditionResult[];
};

export const loadEmailTriggerLogs = async (triggerShortId: string) => {
  const response = await request.get<JourneyEmailTriggerLogList[]>(
    `v1/email-triggers/${triggerShortId}/logs/`
  );
  return response.data;
};

export const loadEmailTriggerLog = async (triggerShortId: string, logShortId: string) => {
  const response = await request.get<JourneyEmailTriggerLog>(
    `v1/email-triggers/${triggerShortId}/logs/${logShortId}/`
  );
  return response.data;
};
